import React, { useContext, useState } from 'react';
import { Api } from '../contexts/Api';
import { Pagination, Modal, Box, Button, Typography, Chip } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

function RecentOrders() {
    const navigate = useNavigate();
    const { orders, handleordersFill, orderPageCount, ordersLoading } = useContext(Api);
    const [open, setOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [activeStatus, setActiveStatus] = useState('');

    const handleDeleteClick = (id) => {
        setOrderToDelete(id);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (orderToDelete) {
            try {
                const response = await fetch('https://api.elnaf.fi/api/removeOrder.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 'id': orderToDelete }),
                });
                const result = await response.json();
                if (result.status === 'success') {
                    alert('Order removed successfully!');
                    navigate('/');
                } else {
                    alert('Error: ' + result.message);
                }
            } catch (error) {
                console.error('Error removing order:', error);
            }
        }
        setOpen(false);
    };

    const handleStatusFilter = (status) => {
        setActiveStatus(status);
        handleordersFill('status', status);
    };

    const statusFilters = [
        { value: '', label: 'All', color: 'default' },
        { value: '0', label: 'Order Placed', color: 'info' },
        { value: '1', label: 'Processing', color: 'warning' },
        { value: '2', label: 'Packing', color: 'warning' },
        { value: '3', label: 'Shipped', color: 'primary' },
        { value: '4', label: 'Delivered', color: 'success' },
        { value: '9', label: 'Cancelled', color: 'error' },
    ];

    const getStatusBadge = (status) => {
        const s = String(status);
        const map = {
            '0': ['info', 'Order Placed'],
            '1': ['warning', 'Processing'],
            '2': ['warning', 'Packing'],
            '3': ['primary', 'Shipped'],
            '4': ['success', 'Delivered'],
            '9': ['danger', 'Cancelled'],
        };
        const [color, label] = map[s] || ['warning', 'Order Placed'];
        return <span className={`badge bg-${color}-transparent`}>{label}</span>;
    };

    const parseProductCount = (productsStr) => {
        try {
            const arr = typeof productsStr === 'string' ? JSON.parse(productsStr) : productsStr;
            return Array.isArray(arr) ? arr.reduce((s, p) => s + parseInt(p.quantity || 1), 0) : 0;
        } catch { return 0; }
    };

    return (
        <div className="card custom-card">
            <div className="card-header justify-content-between">
                <div className="card-title">
                    Recent Orders
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                    {/* Status Filter Chips */}
                    <div className="d-flex gap-1">
                        {statusFilters.map(f => (
                            <Chip
                                key={f.value}
                                label={f.label}
                                size="small"
                                color={activeStatus === f.value ? f.color : 'default'}
                                variant={activeStatus === f.value ? 'filled' : 'outlined'}
                                onClick={() => handleStatusFilter(f.value)}
                                sx={{ cursor: 'pointer', fontWeight: activeStatus === f.value ? 'bold' : 'normal' }}
                            />
                        ))}
                    </div>
                    <div>
                        <input
                            onChange={(e) => handleordersFill('q', e.target.value)}
                            className="form-control form-control-sm"
                            type="text"
                            placeholder="Search orders..."
                            aria-label="Search orders"
                        />
                    </div>
                </div>
            </div>
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table text-nowrap">
                        <thead>
                            <tr>
                                <th scope="col">ID</th>
                                <th scope="col">Full Name</th>
                                <th scope="col">Company</th>
                                <th scope="col">Phone</th>
                                <th scope="col">Email</th>
                                <th scope="col">Items</th>
                                <th scope="col">Shipping</th>
                                <th scope="col">Total</th>
                                <th scope="col">Status</th>
                                <th scope="col">Tracking</th>
                                <th scope="col">Order Date</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordersLoading ? (
                                <tr>
                                    <td colSpan="12" className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : orders && orders.length > 0 ? (
                                orders.map((order, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Link to={`/edit-order/${order.id}`} className="fw-semibold text-primary">
                                                #{order.id}
                                            </Link>
                                        </td>
                                        <td>{order.name + ' ' + order.last_name}</td>
                                        <td>{order.company || '-'}</td>
                                        <td>{order.phone || '-'}</td>
                                        <td>{order.email || '-'}</td>
                                        <td className="text-center">{parseProductCount(order.products)}</td>
                                        <td>
                                            <span className="text-uppercase" style={{ fontSize: '0.75rem' }}>{order.shipping || '-'}</span>
                                            <br />
                                            <small className="text-muted">€{order.shippingPay || '0'}</small>
                                        </td>
                                        <td className="fw-semibold">€{order.total || '0.00'}</td>
                                        <td>{getStatusBadge(order.status)}</td>
                                        <td>
                                            {order.tracking_number ? (
                                                <span className="badge bg-info-transparent" style={{ fontSize: '0.7rem' }}>
                                                    {order.tracking_number}
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>{order.order_date || '-'}</td>
                                        <td>
                                            <div className="btn-list">
                                                <Link to={`/edit-order/${order.id}`} className="btn btn-icon btn-sm btn-primary-light">
                                                    <i className="ri-edit-line" />
                                                </Link>
                                                <button onClick={() => handleDeleteClick(order.id)} className="btn btn-icon btn-sm btn-danger-light product-btn">
                                                    <i className="ri-delete-bin-line" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="12" className="text-center py-4 text-muted">
                                        No orders found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="card-footer">
                <div className="d-flex align-items-center">
                    <div className="ms-auto">
                        <Pagination onChange={(event, value) => handleordersFill('page', value)} count={orderPageCount} variant="outlined" shape="rounded" />
                    </div>
                </div>
            </div>
            <Modal open={open} onClose={handleClose}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: 400,
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" component="h2">
                        Are you sure you want to delete this order?
                    </Typography>
                    <div className="mt-3">
                        <Button onClick={handleClose} color="secondary" sx={{ mr: 2 }}>Cancel</Button>
                        <Button onClick={handleConfirmDelete} variant="contained" color="error">Delete</Button>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}

export default RecentOrders;